import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

import {
  fetchHubServerBuildVersion,
  HUB_CLIENT_BUILD_VERSION,
  HUB_STALE_VERSION_MESSAGE,
  hubBuildVersionsDiffer,
  VERSION_POLL_INTERVAL_MS,
} from "@/lib/hubBuildVersion";
import { HUB_LIFECYCLE_RECONNECTED_EVENT } from "@/lib/hubLifecycleEvents";

/**
 * Persistent top banner when the loaded UI bundle is older than the running API.
 * Checks on mount, window focus, and on a slow interval while the tab is visible.
 * Skips the check when client and server both report `dev` (local development).
 */
export function HubStaleVersionBanner() {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  const checkVersion = useCallback(async () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    const serverVersion = await fetchHubServerBuildVersion();
    if (serverVersion === null) {
      return;
    }
    setVisible(hubBuildVersionsDiffer(HUB_CLIENT_BUILD_VERSION, serverVersion));
  }, []);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current !== undefined) {
        window.clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };

    const schedulePoll = () => {
      clearTimer();
      timerRef.current = window.setInterval(() => {
        void checkVersion();
      }, VERSION_POLL_INTERVAL_MS);
    };

    void checkVersion();
    schedulePoll();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkVersion();
      }
    };

    window.addEventListener("focus", onVisibilityChange);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const onLifecycleReconnected = () => {
      void checkVersion();
    };
    window.addEventListener(HUB_LIFECYCLE_RECONNECTED_EVENT, onLifecycleReconnected);
    return () => {
      window.removeEventListener("focus", onVisibilityChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener(HUB_LIFECYCLE_RECONNECTED_EVENT, onLifecycleReconnected);
      clearTimer();
    };
  }, [checkVersion]);

  if (!visible) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[299] flex justify-center px-4 pt-3"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-950 shadow-sm">
        <RefreshCw className="h-4 w-4 shrink-0 text-sky-700" aria-hidden="true" />
        <span>{HUB_STALE_VERSION_MESSAGE}</span>
        <button
          type="button"
          className="font-medium text-sky-900 underline decoration-sky-400 underline-offset-2 hover:text-sky-950"
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
